// SharpMonitoringGrid.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import "./css/SharpMonitoringGrid.css";
import { apiFetch as fetch } from 'apiClient';

import { DataContext } from "@dashboard/contexts/DataContext";
import { TickProvider } from "@dashboard/contexts/TickContext";
import PriceDifferenceDisplay from "@marketwatch/gridcomponents/guiinstrumentrow/PriceDifferenceDisplay";
import LastTradedPriceDisplay from "@marketwatch/gridcomponents/guiinstrumentrow/LastTradedPriceDisplay";
import { handleSearch, updateTickerSubscriptionWithKite,handleTradingsymbolClick } from "../services/apiService";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";

import { apiFetchJson } from "apiClient";
import ExchangeButtons from "@marketwatch/gridcomponents/components/ExchangeButtons";
import { useGlobalEvents } from "@dashboard/contexts/GlobalEventsContext";
import {useStackedPopup} from "@dashboard/contexts/PopupStackContext";  // NEW
import ReactDOM from "react-dom";
import "../../dashboard/grid-components/css/AnnouncementStyles.css"; // <-- ADJUST PATH IF NEEDED

// Keep percentage inputs between 0 and 100, allow blank
const clampPercent = (value) => {
    if (value === "" || value === null || value === undefined) {
        return "";
    }

    let num = Number(value);
    if (Number.isNaN(num)) {
        return "";
    }

    if (num < 0) num = 0;
    if (num > 100) num = 100;

    return num;
};

const tabID = "sharp-monitoring";

const SharpMonitoringGrid = () => {
    const { allInstrumentsList, allExchangeInstruments } = useContext(DataContext);
    const { sharpMonitoringVersion } = useGlobalEvents();                   // NEW

    const [stocks, setStocks] = useState([]); // backend list
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredInstruments, setFilteredInstruments] = useState([]);
    const [newGridVisible, setNewGridVisible] = useState(false);
    const [intradayMovePercent, setIntradayMovePercent] = useState(5);
    const [multiDayMovePercent, setMultiDayMovePercent] = useState(8);
    const [configWarning, setConfigWarning] = useState("");

    // NEW: popup state for editing params
    const [editingStock, setEditingStock] = useState(null);

    // Load stocks + alerts whenever version bumps (and on mount)
    useEffect(() => {
        const load = async () => {
            try {
                const data = await apiFetchJson("/api/sharp-monitoring/stocks");
                const list = Array.isArray(data) ? data : [];
                setStocks(list);

                // keep ticker subscriptions in sync with current sharp-monitor stocks
                updateTickerSubscriptionWithKite(list, tabID)
                    .then(() => {
                        // optional logging
                    })
                    .catch(error => {
                        console.error('Error in subscription with Kite:', error);
                    });
            } catch (e) {
                console.error("Failed to load sharp monitoring stocks", e);
            }
        };
        load();
    }, [sharpMonitoringVersion]);

    // Search logic
    useEffect(() => {
        handleSearch(searchQuery, allInstrumentsList, setFilteredInstruments, allExchangeInstruments);
    }, [searchQuery, allInstrumentsList]);

    useEffect(() => {
        setNewGridVisible(filteredInstruments.length > 0);
    }, [filteredInstruments]);

    const handleEscape = (event) => {
        if (event.key === "Escape") {
            setNewGridVisible(false);
            setSearchQuery("");
        }
    };

    useEffect(() => {
        if (newGridVisible) {
            document.addEventListener("keydown", handleEscape);
        } else {
            document.removeEventListener("keydown", handleEscape);
        }
        return () => document.removeEventListener("keydown", handleEscape);
    }, [newGridVisible]);

    const handleAddInstrument = async (instrument) => {
        // NEW: require at least one threshold
        const noIntraday =
            intradayMovePercent === "" || intradayMovePercent == null;
        const noMulti =
            multiDayMovePercent === "" || multiDayMovePercent == null;

        if (noIntraday && noMulti) {
            setConfigWarning(
                "Choose at least one: set either Intraday move % or Multi-day move % before adding to sharp monitoring."
            );
            return; // do not add
        }

        try {
            const body = {
                instrumentToken: instrument.instrumentToken,
                tradingsymbol: instrument.tradingsymbol,
                exchange: instrument.exchange,
                intradayMovePercent: noIntraday ? null : intradayMovePercent,
                multidayMovePercent: noMulti ? null : multiDayMovePercent,
            };

            const res = await fetch("/api/sharp-monitoring/stocks", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                throw new Error("Failed to add sharp monitoring stock");
            }
            const saved = await res.json();

            setStocks((prev) => {
                const exists = prev.some((s) => s.id === saved.id);
                if (exists) return prev;
                return [
                    ...prev,
                    {
                        id: saved.id,
                        instrumentToken: saved.instrumentToken,
                        tradingsymbol: saved.tradingsymbol,
                        exchange: saved.exchange,
                        alerts: [],
                        intradayMovePercent: saved.intradayMovePercent,
                        multidayMovePercent: saved.multidayMovePercent,
                        basePrice: saved.basePrice,
                        basePriceCreatedAt: saved.basePriceCreatedAt,
                    },
                ];
            });

            // clear warning once succeed
            setConfigWarning("");
        } catch (e) {
            console.error(e);
        } finally {
            setSearchQuery("");
            setNewGridVisible(false);
        }
    };

    const handleDeleteStock = async (id) => {
        if (!window.confirm("Remove this stock from sharp monitoring?")) return;
        try {
            await fetch(`/api/sharp-monitoring/stocks/${id}`, { method: "DELETE" });
            setStocks((prev) => prev.filter((s) => s.id !== id));
        } catch (e) {
            console.error("Failed to delete sharp monitoring stock", e);
        }
    };

    const handleClearAlert = async (alertId) => {
        try {
            await fetch(`/api/sharp-monitoring/alerts/${alertId}/clear`, { method: "POST" });
            setStocks((prev) =>
                prev.map((s) => {
                    const alerts = Array.isArray(s.alerts) ? s.alerts : [];
                    return {
                        ...s,
                        alerts: alerts.filter((a) => a.id !== alertId),
                    };
                })
            );
        } catch (e) {
            console.error("Failed to clear sharp monitoring alert", e);
        }
    };

    // NEW: update parameters from popup
    const handleUpdateStockParams = async (stockId, payload) => {
        try {
            const updated = await apiFetchJson(`/api/sharp-monitoring/stocks/${stockId}/params`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            setStocks((prev) =>
                prev.map((s) =>
                    s.id === updated.id
                        ? {
                            ...s,
                            basePrice: updated.basePrice,
                            basePriceCreatedAt: updated.basePriceCreatedAt,
                            intradayMovePercent: updated.intradayMovePercent,
                            multidayMovePercent: updated.multidayMovePercent,
                        }
                        : s
                )
            );
        } catch (e) {
            console.error("Failed to update sharp monitoring params", e);
            throw e;
        }
    };

    const isExistingInGrid = (instrumentToken) =>
        stocks.some((s) => Number(s.instrumentToken) === Number(instrumentToken));

    // stocks with alerts at top
    const sortedStocks = useMemo(() => {
        const withAlerts = [];
        const withoutAlerts = [];
        for (const s of stocks) {
            const hasAlerts = Array.isArray(s.alerts) && s.alerts.length > 0;
            if (hasAlerts) withAlerts.push(s);
            else withoutAlerts.push(s);
        }
        return [...withAlerts, ...withoutAlerts];
    }, [stocks]);

    return (
        <div className="sharp-monitoring-wrapper">
        <div className="sharp-monitoring-container">
            {/* Search bar */}
            <div className="sharp-search-input-container">
                <Button
                    className="search-button-1"
                    size="small"
                    onClick={() => {
                        if (!newGridVisible && searchQuery.trim().length === 0) {
                            setNewGridVisible(true);
                        }
                    }}
                >
                    <SearchIcon fontSize="small" />
                </Button>
                <input
                    className="search-input-box"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search & add to SharpMonitoring"
                />
            </div>

            {newGridVisible && (
                <div className="sharp-search-overlay">
                    {/* threshold config row */}
                    <div className="sharp-search-config-row">
                        <div className="sharp-search-config-item">
                            <label className="sharp-search-config-label">
                                1-day move % (vs today close)
                            </label>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                className="sharp-search-config-input"
                                value={intradayMovePercent}
                                onChange={(e) => {
                                    setConfigWarning("");
                                    setIntradayMovePercent(clampPercent(e.target.value));
                                }}
                            />
                        </div>
                        <div className="sharp-search-config-item">
                            <label className="sharp-search-config-label">
                                Multi-day move % (vs base)
                            </label>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                className="sharp-search-config-input"
                                value={multiDayMovePercent}
                                onChange={(e) => {
                                    setConfigWarning("");
                                    setMultiDayMovePercent(clampPercent(e.target.value));
                                }}
                            />
                        </div>
                    </div>

                    {/* NEW: yellow warning message when thresholds not chosen */}
                    {configWarning && (
                        <div className="sharp-config-warning">
                            {configWarning}
                        </div>
                    )}
                    <div className="sharp-search-results">
                    {filteredInstruments.slice(0, 50).map((inst) => {
                        const existing = isExistingInGrid(inst.instrumentToken);

                        return (
                            <div
                                key={inst.instrumentToken}
                                className="sharp-search-row"
                                onClick={
                                    !existing
                                        ? () => handleAddInstrument(inst)
                                        : undefined
                                }
                                style={{
                                    cursor: existing ? "default" : "pointer",
                                    backgroundColor: existing ? "#E0E0E0" : "transparent",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "100%"
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <span
                                            style={{
                                                display: "inline-block",
                                                width: "20px",
                                            }}
                                        >
                                            {existing && <span>✔</span>}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: "13px",
                                                width: "110px",
                                            }}
                                        >
                                            {inst.tradingsymbol}
                                        </span>
                                        <span
                                            style={{
                                                marginLeft: "20px",
                                                fontSize: "10px",
                                            }}
                                        >
                                            {inst.name}
                                        </span>
                                    </div>

                                    <ExchangeButtons exchange={inst.exchange} />
                                </div>
                            </div>
                        );
                    })}
                    </div>
                    {filteredInstruments.length === 0 && (
                        <div className="sharp-search-empty">No instruments match.</div>
                    )}
                </div>
            )}

            {/* Main list */}
            <div className="sharp-grid">
                {sortedStocks.map((s) => {
                    const hasAlerts = Array.isArray(s.alerts) && s.alerts.length > 0;
                    return (
                        <SharpMonitoringRow
                            key={s.id}
                            stock={s}
                            hasAlerts={hasAlerts}
                            onDelete={handleDeleteStock}
                            onClearAlert={handleClearAlert}
                            onEditParams={(stock) => setEditingStock(stock)}
                        />
                    );
                })}
                {sortedStocks.length === 0 && (
                    <div className="sharp-empty">No stocks in sharp monitoring yet.</div>
                )}
            </div>

            {editingStock && (
                <SharpMonitoringParamsPopup
                    stock={editingStock}
                    onClose={() => setEditingStock(null)}
                    onSave={async (payload) => {
                        await handleUpdateStockParams(editingStock.id, payload);
                        setEditingStock(null);
                    }}
                />
            )}
        </div>
        </div>
    );
};

// ---------- Row component with Actions dropdown ----------

const SharpMonitoringRow = ({ stock, hasAlerts, onDelete, onClearAlert, onEditParams }) => {
    const { tradingsymbol, exchange, instrumentToken, alerts = [] } = stock;
    const [priceColor, setPriceColor] = useState('black');
    const [lastPriceColor, setLastPriceColor] = useState('black');
    const { screenerURLs } = useContext(DataContext);

    const [actionsOpen, setActionsOpen] = useState(false);
    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null); // 👈 NEW

    const actionsMenuRef = React.useRef(null);

    useStackedPopup(true, () => {
        setActionsOpen(false);
    });

    useEffect(() => {
        if (!actionsOpen) return;

        const handleClickOutside = (event) => {
            // Close if click is outside menu AND outside the button
            if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target)) {
                setActionsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [actionsOpen]);


    const rowClass = hasAlerts
        ? "sharp-row sharp-row-has-alerts"
        : "sharp-row";

    const handleDeleteClick = () => {
        setActionsOpen(false);
        onDelete(stock.id);
    };

    const handleModifyParamsClick = () => {
        setActionsOpen(false);
        onEditParams(stock);
    };
    const handleAlertMessageClick = (e, alert) => {
        e.preventDefault();
        e.stopPropagation();

        if (alert.alertType === "SCREENER_ANNOUNCEMENT" && alert.stockLink) {
            // Screener → open Screener link in new tab
            window.open(alert.stockLink, "_blank", "noopener,noreferrer");
        } else if (alert.alertType === "EXCHANGE_ANNOUNCEMENT") {
            // BSE announcement → show details popup
            setSelectedAnnouncement(alert);
        }
    };

    return (
        <div className={rowClass}>
            <TickProvider instrumentToken={instrumentToken}>
                <div className="sharp-row-main">
                    <div className="sm-instrument-info-wrapper">
                        <div className="mw-instrument-info sm-instrument-info">
                            <div className="mw-symbol_n_exchange sm-symbol_n_exchange">
                                <span
                                    className="tradingsymbol"
                                    style={{ color: priceColor }}
                                    onClick={() => handleTradingsymbolClick(tradingsymbol,screenerURLs)}
                                >
                                    {tradingsymbol}
                                </span>
                                <span className="exchange-name sm-exchange-name">{exchange}</span>
                            </div>
                            <PriceDifferenceDisplay
                                instrumentToken={instrumentToken}
                                setPriceColor={setPriceColor}
                                setLastPriceColor={setLastPriceColor}
                                contextName={'TickContext'}
                            />
                            <div style={{ width: '5px' }}>
                                <span></span>
                            </div>
                            <div className={"sm-last-traded-price"}>
                            <LastTradedPriceDisplay
                                instrumentToken={instrumentToken}
                                priceColor={lastPriceColor}
                                contextName={'TickContext'}
                            />
                            </div>
                            <div className="sharp-row-actions">
                                <div className="sharp-actions-dropdown" ref={actionsMenuRef}>
                                    <button
                                        className="sharp-actions-button"
                                        onClick={() => setActionsOpen((open) => !open)}
                                        title="Actions"
                                    >
                                        Actions ▾
                                    </button>
                                    {actionsOpen && (
                                        <div className="sharp-actions-menu">
                                            <button
                                                className="sharp-actions-menuitem"
                                                onClick={handleModifyParamsClick}
                                            >
                                                Modify alert parameters
                                            </button>
                                            <button
                                                className="sharp-actions-menuitem sharp-actions-danger"
                                                onClick={handleDeleteClick}
                                            >
                                                Delete from monitoring
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                    </div>
                        <div className="sm-parameters">
                            {/* show only if intradayMovePercent has a value */}
                            {stock.intradayMovePercent != null && (
                                <div className="intraday-move-sm">
                                    <span className="intraday-move-label">Intraday: </span>
                                    <span className="intraday-move-value">
                                        {stock.intradayMovePercent}%
                                    </span>
                                </div>
                            )}

                            {/* show multi-day, base price and base date only if multidayMovePercent has a value */}
                            {stock.multidayMovePercent != null && (
                                <>
                                    <div className="multiday-move-sm">
                                        <span className="multiday-move-label">Multi-day: </span>
                                        <span className="multiday-move-value">
                                            {stock.multidayMovePercent}%
                                        </span>
                                    </div>
                                    <div className="base-price-sm">
                                        <span className="base-price-label">Base price: </span>
                                        <span className="base-price-value">
                                            {stock.basePrice != null ? stock.basePrice : "NA"}
                                        </span>
                                    </div>
                                    <div className="base-price-date-sm">
                                        <span className="base-price-date-label">Base price date: </span>
                                        <span className="base-price-date-value">
                                            {stock.basePriceCreatedAt
                                                ? formatBasePriceDate(stock.basePriceCreatedAt)
                                                : "NA"}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>


                </div>

                {alerts.length > 0 && (
                    <div className="sharp-alerts-strip">
                        {alerts.map((a) => (
                            <div
                                key={a.id}
                                className={`sharp-alert-pill sharp-alert-${a.alertType.toLowerCase()}`}
                            >
                                {/* Column 1: alert source */}
                                <div className="sharp-alert-source-cell">
                                    {getAlertTypeLabel(a)}
                                </div>

                                {/* Column 2: alert message */}
                                <div className="sharp-alert-message-cell">
                                    <button
                                        className="sharp-alert-link"
                                        onClick={(e) => handleAlertMessageClick(e, a)}
                                        title="View announcement"
                                    >
                                        {renderAlertMessage(a)}
                                    </button>
                                </div>

                                {/* Clear button (action, not a data column) */}
                                <button
                                    className="sharp-alert-clear"
                                    onClick={() => onClearAlert(a.id)}
                                    title="Clear this alert"
                                >
                                    Clear
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                {selectedAnnouncement &&
                    ReactDOM.createPortal(
                        <SharpAnnouncementDetailsModal
                            announcement={selectedAnnouncement}
                            onClose={() => setSelectedAnnouncement(null)}
                            onClear={
                                selectedAnnouncement.id
                                    ? () => onClearAlert(selectedAnnouncement.id)
                                    : undefined
                            }
                        />,
                        document.getElementById("modal-root")
                    )}

            </TickProvider>
        </div>
    );
};

const getAlertTypeLabel = (a) => {
    switch (a.alertType) {
        case "EXCHANGE_ANNOUNCEMENT":
            return "BSE announcement";
        case "SCREENER_ANNOUNCEMENT":
            return "Screener announcement";
        case "BIG_PRICE_CHANGE":
            return "Price move";
        default:
            return a.alertType || "Alert";
    }
};

const renderAlertMessage = (a) => {
    switch (a.alertType) {
        case "EXCHANGE_ANNOUNCEMENT":
            return a.message ?? "";
        case "SCREENER_ANNOUNCEMENT":
            return a.message ?? "";
        case "BIG_PRICE_CHANGE":
            return a.message ??
                `${a.tradingsymbol} moved >= ${a.thresholdPercent}%`;
        default:
            return a.message ?? "Alert";
    }
};

// ---------- NEW POPUP COMPONENT ----------

const toInputDateTimeLocal = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).toString().padStart(2, "0");
    const yyyy = d.getFullYear();
    const MM = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
};

const SharpMonitoringParamsPopup = ({ stock, onClose, onSave }) => {
    const [basePrice, setBasePrice] = useState(
        stock.basePrice != null ? stock.basePrice : ""
    );
    const [basePriceCreatedAt, setBasePriceCreatedAt] = useState(
        toInputDateTimeLocal(stock.basePriceCreatedAt)
    );
    const [intradayMovePercent, setIntradayMovePercent] = useState(
        stock.intradayMovePercent != null ? stock.intradayMovePercent : ""
    );
    const [multidayMovePercent, setMultidayMovePercent] = useState(
        stock.multidayMovePercent != null ? stock.multidayMovePercent : ""
    );
    const [saving, setSaving] = useState(false);

    useStackedPopup(true, () => {
        onClose();
    });

    const handleSubmit = async () => {
        setSaving(true);
        try {
            const payload = {
                basePrice: basePrice === "" ? null : Number(basePrice),
                basePriceCreatedAt:
                    basePriceCreatedAt && basePriceCreatedAt !== ""
                        ? new Date(basePriceCreatedAt).toISOString()
                        : null,
                intradayMovePercent:
                    intradayMovePercent === "" ? null : clampPercent(intradayMovePercent),
                multidayMovePercent:
                    multidayMovePercent === "" ? null : clampPercent(multidayMovePercent),
            };
            await onSave(payload);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="sharp-params-overlay">
            <div className="sharp-params-modal">
                <div className="sharp-params-header">
                    <span>Modify alert parameters — {stock.tradingsymbol}</span>
                    <button
                        className="sharp-params-close-btn"
                        onClick={onClose}
                        disabled={saving}
                    >
                        ×
                    </button>
                </div>

                <div className="sharp-params-body">
                    <div className="sharp-params-row">
                        <label className="sharp-params-label">Base price</label>
                        <input
                            type="number"
                            className="sharp-params-input"
                            value={basePrice}
                            onChange={(e) => setBasePrice(e.target.value)}
                        />
                    </div>

                    <div className="sharp-params-row">
                        <label className="sharp-params-label">Base price created at</label>
                        <input
                            type="datetime-local"
                            className="sharp-params-input"
                            value={basePriceCreatedAt}
                            onChange={(e) => setBasePriceCreatedAt(e.target.value)}
                        />
                    </div>

                    <div className="sharp-params-row">
                        <label className="sharp-params-label">
                            1-day move % (vs today close)
                        </label>
                        <input
                            type="number"
                            min="0"
                            max="100"
                            className="sharp-params-input"
                            value={intradayMovePercent}
                            onChange={(e) =>
                                setIntradayMovePercent(clampPercent(e.target.value))
                            }
                        />
                    </div>

                    <div className="sharp-params-row">
                        <label className="sharp-params-label">
                            Multi-day move % (vs base)
                        </label>
                        <input
                            type="number"
                            min="0"
                            max="100"
                            className="sharp-params-input"
                            value={multidayMovePercent}
                            onChange={(e) =>
                                setMultidayMovePercent(clampPercent(e.target.value))
                            }
                        />
                    </div>
                </div>

                <div className="sharp-params-footer">
                    <button
                        className="sharp-params-btn sharp-params-btn-secondary"
                        onClick={onClose}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        className="sharp-params-btn sharp-params-btn-primary"
                        onClick={handleSubmit}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SharpMonitoringGrid;

const formatBasePriceDate = (value) => {
    if (!value) return "";

    const d = new Date(value);
    if (isNaN(d.getTime())) return value; // fallback if malformed

    const day = d.getDate();
    const month = d.toLocaleString("en-US", { month: "short" }); // "Nov"
    const year = d.getFullYear();

    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");

    return `${day} ${month} ${year} ${hours}:${minutes}`;
};

const SharpAnnouncementDetailsModal = ({ announcement, onClose, onClear }) => {
    if (!announcement) return null;

    // we’ll show Clear if onClear is provided
    const showClear = typeof onClear === "function";

    return (
        <div className="announcement-modal">
            <div className="announcement-annpopup">
                <div className="annpopup-header">
                    <h4>Announcement Details</h4>
                    <button
                        onClick={onClose}
                        className="annpopup-close"
                    >
                        &times;
                    </button>
                </div>

                <div className="annpopup-content-table">
                    <div className="annpopup-table">
                        {Object.entries(announcement).map(([key, value]) => (
                            <div key={key} className="annpopup-row">
                                <div className="annpopup-cell annpopup-label">
                                    {key.charAt(0).toUpperCase() + key.slice(1)}:
                                </div>
                                <div className="annpopup-cell annpopup-value">
                                    {key.toLowerCase() === "extradata" ? (
                                        <a
                                            href={value}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {value}
                                        </a>
                                    ) : (
                                        value != null
                                            ? value.toString()
                                            : ""
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="contract-buttons">
                    <button
                        className="annpopup-clear-button"
                        style={{ visibility: showClear ? "visible" : "hidden" }}
                        onClick={() => {
                            if (!showClear) return;
                            onClear();
                            onClose();
                        }}
                    >
                        Clear
                    </button>
                    <button onClick={onClose} className="cancel-button">
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

